import {useTranslation} from 'react-i18next';
import {useSelector} from 'react-redux';
import {getDate, getHourMinutes} from '../../library/dates';
import {useEffect} from 'react';
import {getEventGroups} from '../../redux/tickets/ticketsActions';
import {useParams} from 'react-router-dom';

export const variableTexts = {
    givechak: {
        eventLink: 'givechakLink',
        eventTitle: 'givechakTitle'
    },
    subscription: {
        eventTitle: 'subscriptionEventTitle',
        eventLink: 'subscriptionEventLink',
        ticketName: 'subscriptionTicketName',
        ticketText: 'subscriptionTicketText'
    }
};

const applyEventTypeVariableLabels = (vars, eventType, t) => {
    const typeTexts = variableTexts[eventType];
    if(!typeTexts) return vars;

    const overrides = Object.fromEntries(
        Object.entries(typeTexts)
            .filter(([key]) => key in vars)
            .map(([key, translationKey]) => [key, t(translationKey)])
    );

    return {...vars, ...overrides};
};

const getTimeVariables = (event, branding, t) => {
    const {currentLang, data} = branding;
    return {
        timeOpenstartDate: event?.timeStart ? getDate(event.timeStart) : '',
        timeOpenstartDateHeb: data?.[currentLang]?.date?.hebrewStartDate || data?.[currentLang]?.date?.hebrewDate || '',
        timeOpenstartTime: event?.timeOpen ? getHourMinutes(event.timeOpen) : '',
        timeStart: event?.timeStart ? getHourMinutes(event.timeStart) : '',
        timeOpenstartText: `${t('timeOpenstartText')}: `
    };
};

const getGivechakVariables = (event, t, type) => {
    if(event?.type !== 'givechak') return {};
    return {
        refName: t('refName'),
        refTotal: t('refTotal'),
        refTarget: t('refTarget'),
        refBalance: t('refBalance'),
        refLink: t('refLink'),
        ...(type !== 'lead' && {sendWhatsapp: t('sendWhatsappValue')}),
        ...(type !== 'lead' && {donation: t('donation')}),
    };
};

const getSuspendedVariables = (t) => ({
    billingLineSuspended: t('billingLineSuspendedText')
});

const getTicketVariables = (event, tickets, t) => {
    const ticketTitle = tickets?.tickets?.length > 0 ? tickets.tickets[0]?.title : '';
    const ticketLinkType = event?.type === 'subscription' ? 'ticketLinkSeriesType' : 'ticketLinkTicketType';
    return {
        ticketLink: t('ticketLink', {text: t(ticketLinkType)}),
        ticketLinkPrint: t('ticketLinkPrint'),
        ticketName: ticketTitle,
        ...(ticketTitle && {ticketNameAmount: t('ticketNameAmount')}),
        extraName: t('extraName'),
        extraNameAmount: t('extraNameAmount'),
        externalVoucher: t('externalVoucherValue'),
        ticketText: tickets?.tickets?.[0]?.text || t('ticketTextValue'),
        ...(tickets?.groups?.length > 0 && {groupName: tickets.groups[0]?.title})
    };
};

const SUBSCRIPTION_ALLOWED_VARIABLES = [
    'eventTitle', 'eventLink', 'nameFirst', 'nameLast',
    'ticketLink', 'ticketName', 'ticketText',
    'invoiceLink', 'currencySymbol', 'billingLine',
    'producerName', 'producerPhone', 'portalLink',
    'eventsAmount', 'oid'
];

const SERIES_ALLOWED_VARIABLES = [
    'eventTitle', 'eventLink',
    'ticketName',
    'producerName', 'producerPhone',
    'eventsAmount'
];

const SUBSCRIPTION_LANDING_ALLOWED_VARIABLES = [
    'eventTitle', 'eventLink',
    'currencySymbol',
    'producerName', 'producerPhone',
    'portalLink',
    'eventsAmount'
];

const getBaseVariables = (event, user, producer, t) => ({
    eventTitle: event?.title || '',
    nameFirst: t('nameFirst'),
    nameLast: t('nameLast'),
    eventLink: t('eventLink'),
    invoiceLink: t('invoiceLink'),
    currencySymbol: event?.currencySymbol || '',
    eventCurrency: event?.currencySymbol || '',
    billingLine: `${t('billingLineText')} ${event?.supplierName}`,
    producerName: producer?.contactName || t('producerName'),
    producerPhone: producer?.contactPhone || t('producerPhone'),
    portalLink: event?.type === 'subscription' ? t('subscriptionPersonalLink') : t('portalLink')
});

const getLocationVariables = (event) => ({
    ...(event?.location && {location: event.location}),
    ...(event?.locationMap && {waze: event.waze})
});

const getAreaVariables = (event, t) => ({
    ...(event?.type === 'area' && {seats: t('yourSeats')})
});

const getInvoiceVariables = (invoicePostFields, user, event, t) => {
    return {
        invoiceSum: invoicePostFields?.sum || t('invoiceSum'),
        invoicePayment: invoicePostFields?.payment || t('invoicePayment'),
        invoiceBillingLink: invoicePostFields?.invoice || t('invoiceLink'),
        currencySymbol: invoicePostFields?.currency || event?.currencySymbol || t('currencySymbol'),
        invoiceCustomerName: invoicePostFields?.name ||`${ user?.nameFirst} ${user?.nameLast}` || t('invoiceCustomerName'),
        invoiceMonth: invoicePostFields?.month || t('invoiceMonth'),
    };
};

const subscriptionVariables = (t) => ({
    eventsAmount: t('eventsAmount'),
    oid: '123456',
});

export function useVariables(type = 'ticket') {
    const event = useSelector(s => s.event);
    const user = useSelector(s => s.user);
    const tickets = useSelector(s => s.tickets);
    const producer = useSelector(s => s.account.packagesProducer);
    const branding = useSelector(s => s.branding ?? {});
    const account = useSelector(s => s.account);
    const invoicePostFields = useSelector(s => s.cash?.invoicePostFields);
    const {t} = useTranslation('variables');
    const {messageType} = useParams();

    const susStatus = event?.settings?.suspended?.susStatus;
    const suspendedStatus = account?.permissions?.find(item => item.permission === 'suspended')?.status;
    const isSuspended = susStatus && suspendedStatus === 1;
    const isGivechak = event?.type === 'givechak';

    const timeVariables = !isGivechak ? getTimeVariables(event, branding, t) : {};
    const givechakVariables = getGivechakVariables(event, t, type);
    const suspendedVariables = getSuspendedVariables(t);
    const baseVariables = getBaseVariables(event, user, producer, t);
    const locationVariables = getLocationVariables(event);
    const areaVariables = getAreaVariables(event, t);
    const isSubscription = event?.type === 'subscription';
    const ticketVariables = !isGivechak ? getTicketVariables(event, tickets, t) : {};
    const invoiceVariables = getInvoiceVariables(invoicePostFields, user, event, t);

    const leadVariables = {
        eventTitle: event?.title || '',
        ...givechakVariables
    };

    const invoiceVariablesList = {
        eventTitle: event?.title || '',
        producerName: producer?.contactName || '',
        producerPhone: producer?.contactPhone || '',
        portalLink: t('portalLink'),
        ...invoiceVariables
    };

    const billingVariables = {
        ...baseVariables,
        ...invoiceVariables
    };
    delete billingVariables.invoiceLink;

    const allVariables = {
        ...baseVariables,
        ...locationVariables,
        ...(event?.timeStart && timeVariables),
        ...areaVariables,
        ...(isSuspended && suspendedVariables),
        ...givechakVariables,
        ...ticketVariables,
        ...(messageType === 'desperate' && {advancedLink: t('advancedLink')}),
        ...(isSubscription && subscriptionVariables(t))
    };

    const filterByAllowed = (vars, allowed) => 
        Object.fromEntries(Object.entries(vars).filter(([key]) => allowed.includes(key)));

    const variablesByType = {
        lead: leadVariables,
        invoice: invoiceVariablesList,
        billing: billingVariables
    };

    let defaultVariables = variablesByType[type] ?? variablesByType[messageType] ?? allVariables;
    if(type === 'series') {
        defaultVariables = filterByAllowed(allVariables, SERIES_ALLOWED_VARIABLES);
    } else if(type === 'subscriptionDescription') {
        defaultVariables = filterByAllowed(allVariables, SUBSCRIPTION_LANDING_ALLOWED_VARIABLES);
    } else if(isSubscription && !variablesByType[type]) {
        defaultVariables = filterByAllowed(allVariables, SUBSCRIPTION_ALLOWED_VARIABLES);
    }

    const eventType = event?.type;
    if(eventType && variableTexts[eventType]) {
        defaultVariables = applyEventTypeVariableLabels(defaultVariables, eventType, t);
    }

    const disabledVariables = type === 'lead' || type === 'invoice' || isSubscription ? {} : {
        ...(!event?.timeStart && timeVariables),
        ...(!event?.location && {location: event?.location}),
        ...(!event?.locationMap && {waze: event?.waze}),
        ...((susStatus && !isSuspended) && suspendedVariables),
        ...(event?.type !== 'givechak' && !tickets?.tickets?.[0]?.title && {ticketNameAmount: ''})
    };

    useEffect(() => {
        if(type && !tickets?.getEventGroupsXHR) {
            getEventGroups().then();
        }
    });

    return {defaultVariables, disabledVariables, invoiceVariables};
}
